import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const inp = "w-full rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]";
const CC = [["TAF", "TAF Villas"], ["TRIBE", "TRIBE Beach Bar"], ["TERA", "TERA Wedding Venue"], ["SHARED", "Κοινά / Διοίκηση"]];
const KEYS = ["finance-accounts", "custom-accounts", "expense-lines", "income-budget", "expense-budget-monthly", "pl-own", "pl-budget", "pl-budget-full", "budget-vs-actual"];

// Adds a new income or expense line (shared by Budget and P&L — same code list everywhere).
export const AddLineButton = ({ kind = "income", className = "" }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", cost_center: "SHARED", keywords: "", account_code: "", below: false });
  const { data: meta } = useQuery({ queryKey: ["finance-accounts"], queryFn: async () => (await api.get("/finance/accounts")).data, enabled: open && kind === "expense" });
  const isIncome = kind === "income";
  const save = useMutation({
    mutationFn: async () => (isIncome
      ? (await api.post("/finance/custom-accounts", { name: f.name, section: "revenue", cost_center: f.cost_center, keywords: f.keywords.split(",").map((k) => k.trim()).filter(Boolean) })).data
      : (await api.post("/finance/expense-lines", { label: f.name, account_code: f.account_code, cost_center: f.cost_center, below: f.below })).data),
    onSuccess: (d) => {
      KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      qc.invalidateQueries();
      toast.success(`Νέα γραμμή ${d.code} ${d.name || d.label} — εμφανίζεται στον Προϋπολογισμό και στο P&L`);
      setOpen(false); setF({ name: "", cost_center: "SHARED", keywords: "", account_code: "", below: false });
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const accounts = (meta?.accounts || []).filter((a) => a.section !== "revenue");
  return (
    <>
      <button data-testid={`add-line-${kind}`} onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 rounded-xl border border-dashed border-[#B9AE9A] bg-white px-4 py-2.5 text-sm font-semibold text-[#45423C] hover:border-[#C86235] hover:text-[#C86235] ${className}`}>
        <Plus className="h-4 w-4" /> {isIncome ? "Νέα γραμμή εσόδων" : "Νέα γραμμή εξόδων"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-line-dialog">
          <DialogHeader><DialogTitle>{isIncome ? "Νέα πηγή εσόδων" : "Νέα κατηγορία εξόδων"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="block"><span className="block text-[10px] uppercase tracking-wider text-[#6F6A60] mb-1">Όνομα</span>
              <input data-testid="add-line-name" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={isIncome ? "π.χ. Έσοδα Spa" : "π.χ. Συνδρομές λογισμικού"} autoFocus /></label>
            <label className="block"><span className="block text-[10px] uppercase tracking-wider text-[#6F6A60] mb-1">Κέντρο κόστους</span>
              <select data-testid="add-line-cc" className={inp} value={f.cost_center} onChange={(e) => setF({ ...f, cost_center: e.target.value })}>{CC.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
            {isIncome ? (
              <label className="block"><span className="block text-[10px] uppercase tracking-wider text-[#6F6A60] mb-1">Λέξεις-κλειδιά Hotelizer (προαιρετικά, με κόμμα)</span>
                <input data-testid="add-line-keywords" className={inp} value={f.keywords} onChange={(e) => setF({ ...f, keywords: e.target.value })} placeholder="π.χ. SPA, MASSAGE" />
                <span className="block text-[11px] text-[#8A8577] mt-1">Παραστατικά πωλήσεων με αυτές τις λέξεις κατατάσσονται αυτόματα εδώ.</span></label>
            ) : (
              <>
                <label className="block"><span className="block text-[10px] uppercase tracking-wider text-[#6F6A60] mb-1">Λογαριασμός USALI (ομαδοποίηση)</span>
                  <select data-testid="add-line-account" className={inp} value={f.account_code} onChange={(e) => setF({ ...f, account_code: e.target.value })}>
                    <option value="">— επιλέξτε —</option>{accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                  </select></label>
                <label className="flex items-center gap-2 text-sm text-[#45423C]"><input type="checkbox" data-testid="add-line-below" checked={f.below} onChange={(e) => setF({ ...f, below: e.target.checked })} /> Κάτω από το EBITDA (επένδυση / ιδιοκτήτη)</label>
              </>
            )}
          </div>
          <DialogFooter>
            <button data-testid="add-line-cancel" onClick={() => setOpen(false)} className="rounded-xl border border-[#E3DED4] px-4 py-2 text-sm">Άκυρο</button>
            <button data-testid="add-line-save" disabled={save.isPending || !f.name.trim() || (!isIncome && !f.account_code)} onClick={() => save.mutate()}
              className="rounded-xl bg-[#45423C] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{save.isPending ? "Δημιουργία…" : "Δημιουργία"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
